import { useEffect, useRef, useState } from "react";
import styles from "./ObjectDetectionOverlay.module.css";

const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, Number(value) || 0));

const humanizeLabel = (value) => String(value || "Object")
	.replace(/[_-]+/g, " ")
	.replace(/\b\w/g, (character) => character.toUpperCase());

const normalizedBox = (object) => {
	const source = object?.boundingBox;
	if (!source) return null;
	const x = clamp(source.x);
	const y = clamp(source.y);
	const width = clamp(source.width, 0, 1 - x);
	const height = clamp(source.height, 0, 1 - y);
	if (width <= 0 || height <= 0) return null;
	return { x, y, width, height };
};

const objectKey = (object, index) => object?._id || [
	object?.label,
	object?.boundingBox?.x,
	object?.boundingBox?.y,
	index
].join(":");

/**
 * Draws the normalized boxes returned by the general Media ML detector over
 * the fitted lightbox image. The detector works on a resized copy, but its
 * saved boxes are already normalized to 0..1, so they map directly here.
 */
export default function ObjectDetectionOverlay({ slide, objects = [], visibleCount = 0 }) {
	const containerRef = useRef(null);
	const [containerSize, setContainerSize] = useState(null);
	const [activeKey, setActiveKey] = useState(null);

	useEffect(() => {
		const element = containerRef.current;
		if (!element || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry) return;
			setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
		});
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		setActiveKey(null);
	}, [slide?.id]);

	const imageWidth = Number(slide?.width) || Number(slide?.mediaAnalysis?.image?.width) || 0;
	const imageHeight = Number(slide?.height) || Number(slide?.mediaAnalysis?.image?.height) || 0;
	const canPosition = containerSize && imageWidth > 0 && imageHeight > 0;
	const visibleObjects = objects.slice(0, Math.max(0, visibleCount));

	let imageRect = null;
	if (canPosition) {
		const scale = Math.min(containerSize.width / imageWidth, containerSize.height / imageHeight);
		const width = imageWidth * scale;
		const height = imageHeight * scale;
		imageRect = {
			left: (containerSize.width - width) / 2,
			top: (containerSize.height - height) / 2,
			width,
			height
		};
	}

	return (
		<div ref={containerRef} className={styles.overlay} aria-live="polite">
			{imageRect && visibleObjects.map((object, index) => {
				const box = normalizedBox(object);
				if (!box) return null;
				const key = objectKey(object, index);
				const isPerson = String(object.label || "").toLowerCase() === "person";
				const isActive = activeKey === key;
				const label = humanizeLabel(object.label);
				const colorName = object.color?.name ? humanizeLabel(object.color.name) : "";
				const confidence = Number(object.confidence);
				const confidenceText = Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : "";
				const detail = [colorName, label, confidenceText].filter(Boolean).join(" · ");
				const renderedTop = imageRect.top + box.y * imageRect.height;
				const placeLabelBelow = renderedTop < 42;

				return (
					<button
						type="button"
						key={key}
						className={`${styles.objectBox} ${isPerson ? styles.personBox : ""} ${isActive ? styles.active : ""}`}
						style={{
							left: imageRect.left + box.x * imageRect.width,
							top: imageRect.top + box.y * imageRect.height,
							width: box.width * imageRect.width,
							height: box.height * imageRect.height
						}}
						aria-label={`Detected ${detail || label}`}
						title={detail || label}
						onClick={(event) => {
							event.stopPropagation();
							setActiveKey(isActive ? null : key);
						}}
					>
						<span className={`${styles.label} ${placeLabelBelow ? styles.labelBelow : ""}`}>
							{object.color?.hex && (
								<span
									className={styles.colorSwatch}
									style={{ backgroundColor: object.color.hex }}
									aria-hidden="true"
								/>
							)}
							<span>{colorName ? `${colorName} ${label}` : label}</span>
							{confidenceText && <span className={styles.confidence}>{confidenceText}</span>}
						</span>
					</button>
				);
			})}
		</div>
	);
}
